import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getData } from "../global/Globalapi"; // Keeping your API import
import { getBaseUrl } from "../global/Globalapi"; // ✅ Importing getBaseUrl function

import {
  ArrowLeft,
  Package,
  Truck,
  CheckCircle,
  MapPin,
  Clock,
  // CreditCard,
} from "lucide-react";

const OrderTracking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch Order Logic
  useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getData(`/orders/${orderId}`);
        console.log(
          "OrderTracking backend response:",
          response.data || response,
        );
        setOrderData(response.data || response);
      } catch (err) {
        setError("Order not found or server error.");
      } finally {
        setLoading(false);
      }
    };
    if (orderId) fetchOrder();
  }, [orderId]);

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Tracking your order...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !orderData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-sm text-center max-w-md w-full border border-gray-100">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package size={24} />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Order Not Found
          </h2>
          <p className="text-gray-500 mb-6">
            We couldn't locate the details for this order ID.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="w-full py-2.5 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  const getStepIndexFromStatus = (status) => {
    switch (status) {
      case "Success":
      case "Ordered":
      case "Pending":
        return 1; // Green line till Processing

      case "Processing":
        return 1;

      case "Shipped":
        return 2; // Green line till Shipped

      case "Delivered":
        return 3; // Full green till Delivered

      case "Cancelled":
        return 0; // Only order placed

      default:
        return 0;
    }
  };

  // Configuration for Steps
  // Use only backend values, no defaults
  // const currentStepIndex = orderData.currentStep;
  const currentStepIndex = getStepIndexFromStatus(orderData.status);

  const steps = [
    { label: "Order Placed", date: orderData.placed_at, icon: Package },
    { label: "Processing", date: orderData.processing_at, icon: Clock },
    {
      label: "Shipped/Ready for Delivery",
      date: orderData.shipped_at,
      icon: Truck,
    },
    { label: "Delivered", date: orderData.delivered_at, icon: CheckCircle },
  ];

  // Helper to unify product list (handles 'products' or 'items' array)
  const productList = orderData.products || orderData.items || [];

  const productTotal = productList.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0,
  );

  const finalTotal = Number(orderData.payedamount || orderData.total || 0);

  const deliveryCharge = finalTotal - productTotal;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Track Order</h1>
            <p className="text-sm text-gray-500 mt-1">
              Order ID:{" "}
              <span className="font-mono font-medium text-gray-900">
                {orderData.order_id || orderData.id || orderId}
              </span>
            </p>
          </div>

          {/* UPDATED BUTTON STYLE: Professional Dark Outline */}
          <button
            onClick={() => navigate(-1)}
            className="mt-4 sm:mt-0 flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
          >
            <ArrowLeft size={16} />
            Return to Orders
          </button>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Progress Tracker Section */}
          <div className="p-6 sm:p-12 border-b border-gray-100">
            <div className="relative">
              {/* Gray Background Line */}
              <div className="hidden sm:block absolute top-1/2 left-0 w-full h-1 bg-gray-200 -translate-y-1/2 rounded-full -z-10"></div>

              {/* GREEN LINE (Dynamic Width) */}
              {/* <div
                className="hidden sm:block absolute top-1/2 left-0 h-1 bg-emerald-500 -translate-y-1/2 rounded-full -z-10 transition-all duration-700 ease-out"
                style={{
                  width: `${(currentStepIndex / (steps.length - 1)) * 100}%`,
                }}
              ></div> */}
              {/* Background line */}
              <div className="hidden sm:block absolute left-0 right-0 top-7 h-[3px] bg-gray-200 z-0"></div>

              {/* Active line */}
              <div
                className="hidden sm:block absolute left-0 top-7 h-[3px] bg-emerald-500 z-0 transition-all duration-700"
                style={{
                  width: `${(currentStepIndex / (steps.length - 1)) * 100}%`,
                }}
              ></div>

              {/* Steps */}
              <div className="flex flex-col sm:flex-row justify-between gap-8 sm:gap-0">
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  const isCompleted = index <= currentStepIndex;
                  const isCurrent = index === currentStepIndex;

                  return (
                    <div
                      key={index}
                      className="flex sm:flex-col items-center gap-4 sm:gap-2 relative"
                    >
                      {/* Vertical Line for Mobile only */}
                      {index !== steps.length - 1 && (
                        <div
                          className={`sm:hidden absolute left-6 top-10 w-0.5 h-12 ${isCompleted ? "bg-emerald-500" : "bg-gray-200"}`}
                        ></div>
                      )}

                      {/* Icon Circle */}
                      <div
                        className={`
                        w-12 h-12 flex items-center justify-center rounded-full z-10 transition-all duration-300 border-2
                        ${
                          isCompleted
                            ? "bg-emerald-500 border-emerald-500 text-white shadow-md" // Filled Green for completed
                            : "bg-white border-gray-200 text-gray-400" // Gray for pending
                        }
                        ${isCurrent ? "ring-4 ring-emerald-100 scale-110" : ""}
                      `}
                      >
                        <Icon size={20} strokeWidth={isCompleted ? 3 : 2} />
                      </div>

                      {/* Text Details */}
                      <div className="sm:text-center">
                        <p
                          className={`text-sm font-bold ${isCompleted ? "text-gray-900" : "text-gray-500"}`}
                        >
                          {step.label}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5 font-medium">
                          {step.date}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">
            {/* Delivery Address */}
            <div className="p-6 sm:p-8 bg-gray-50/50">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <MapPin size={14} className="text-emerald-500" /> Delivery
                Address
              </h3>
              <div className="text-sm text-gray-700 space-y-1">
                <p className="font-semibold text-gray-900 mb-2">
                  {orderData.customer_name}
                </p>
                <p className="leading-relaxed">{orderData.address}</p>
              </div>
            </div>

            {/* Order Items */}
            <div className="p-6 sm:p-8 col-span-2">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Package size={14} className="text-emerald-500" /> Order Items
              </h3>

              <div className="space-y-4">
                {productList.length > 0 ? (
                  productList.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between items-start group"
                    >
                      <div className="flex items-start gap-4">
                        {/* Product Image Placeholder */}
                        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 shrink-0">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt=""
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            <Package size={24} />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {item.product_name || item.name}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Quantity: {item.quantity || item.qty}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm font-bold text-gray-900">
                        ₹{item.amount || item.price}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    No items found.
                  </p>
                )}

                 {deliveryCharge > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 font-medium">
                      Delivery Charges
                    </span>
                    <span className="font-semibold text-gray-900">
                      ₹{deliveryCharge.toFixed(2)}
                    </span>
                  </div>
                )}

                <div className="pt-6 mt-6 border-t border-gray-100 flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">
                    Total Amount
                  </span>
                  <span className="text-xl font-bold text-gray-900">
                    ₹{finalTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          {/* <div className="bg-gray-50 p-4 sm:px-8 py-4 flex flex-col sm:flex-row justify-between items-center text-sm border-t border-gray-100 gap-3 sm:gap-0"> */}
          {/* <div className="flex items-center gap-2 text-gray-500">
              <CheckCircle size={16} className="text-emerald-500" />
              <span>
                Payment Status:{" "}
                <span className="font-medium text-gray-900">Paid</span>
              </span>
            </div> */}

          {/* <button
              onClick={() => {
                const invoiceFile = `${orderId}.pdf`;
                const baseUrl = window.location.hostname.includes("localhost")
                  ? "http://localhost:5000"
                  : "https://your-production-domain.com";

                window.location.href = `${baseUrl}/generated/${invoiceFile}`;
              }}
              className="text-emerald-600 font-semibold"
            >
              Download Receipt
            </button> */}
          {/* <button
              onClick={() => {
                const invoiceFile = `${orderId}.pdf`;
                const baseUrl = getBaseUrl();

                window.location.href = `${baseUrl}/generated/${invoiceFile}`;
              }}
              className="text-emerald-600 font-semibold"
            >
              Download Receipt
            </button> */}
          {/* </div> */}
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
